/**
 * The focus goal as Home shows it (TMMAPP-0152 §A 7–8), shared by the Your focus tile and the
 * Your focus goal suggestion. The goal is `focusGoalOf` (the newest open GOAL with an envelope, in
 * scope); its percentage is the envelope's served `progressBp` (rounded down, so a goal never reads
 * 100 % early), its balance the item's `progressMinor` and its target the envelope's `targetMinor`,
 * in the goal's virtual account's currency. Nothing is computed from rows.
 */
import type { TFunction } from 'i18next';
import type { MeVirtualAccount } from '@/api/me';
import { formatMoneyMinor, formatPercent } from '@/i18n/format';
import { dataOf, type HomeData } from './useHomeData';

export interface FocusView {
  /** Its envelope page. */
  to: string;
  /** The envelope's name (else the goal's text). */
  name: string;
  /** "60%", or null when the envelope serves no progress. */
  pct: string | null;
  /** "60% there · $6,000.00 of $10,000.00", or null when a part isn't served. */
  line: string | null;
}

/** `undefined` while the goals (or the goal's envelope) load; `null` when there is no goal. */
export function focusViewOf(
  data: HomeData,
  virtualAccounts: readonly MeVirtualAccount[],
  t: TFunction,
  locale: string,
): FocusView | null | undefined {
  if (data.items.read.status === 'loading') return undefined;
  const goal = data.focusGoal;
  if (goal === null || goal.envelopeId === null) return null;
  if (data.focusEnvelope.read.status === 'loading') return undefined;
  const envelope = dataOf(data.focusEnvelope.read);
  const currency =
    virtualAccounts.find((row) => row.id === goal.virtualAccountId)?.currency ?? null;
  const bp = envelope?.progressBp ?? null;
  const pct = bp === null ? null : formatPercent(Math.floor(Math.max(0, bp) / 100) / 100, locale);
  const target = envelope?.targetMinor ?? null;
  const line =
    pct !== null && target !== null && goal.progressMinor !== null && currency !== null
      ? t('home.suggest.focus.body', {
          pct,
          balance: formatMoneyMinor(goal.progressMinor, currency, locale, false, { whole: true }),
          target: formatMoneyMinor(target, currency, locale, false, { whole: true }),
        })
      : null;
  return {
    to: `/v/${goal.virtualAccountId}/e/${goal.envelopeId}`,
    name: envelope?.name ?? goal.envelopeName ?? goal.body,
    pct,
    line,
  };
}
